/**
 * Vocabulary of the reports module (HS-961): label keys, badge colours and the small predicates the
 * screens share. Contract shapes live in `reports.types.ts`; this file only reads them.
 */
import type { BadgeColor, IconName } from '@smartsash/ui';

import { ACTIVE_RUN_STATUSES } from './reports.constants';
import type {
  DeliveryStatus,
  FileState,
  ReportFormat,
  ReportRunListItemDto,
  ReportTemplateDto,
  RunSource,
  RunStatus,
  ScheduleFrequency,
  ScheduleStatus,
  ScopeRefDto,
} from './reports.types';

import type { TranslationKey } from '@/types/i18n';

export const RUN_STATUS_LABEL_KEYS = {
  Queued: 'reports.statuses.Queued',
  Running: 'reports.statuses.Running',
  Completed: 'reports.statuses.Completed',
  Failed: 'reports.statuses.Failed',
  Expired: 'reports.statuses.Expired',
} as const satisfies Record<RunStatus, TranslationKey>;

/**
 * Ready reads as done, in-progress as information, and both dead ends — Failed and Expired — as an
 * error (Figma `1495:33718`, X3): neither has a file to download.
 */
export const RUN_STATUS_COLORS = {
  Queued: 'gray',
  Running: 'blue',
  Completed: 'success',
  Failed: 'error',
  Expired: 'error',
} as const satisfies Record<RunStatus, BadgeColor>;

export const RUN_SOURCE_LABEL_KEYS = {
  Manual: 'reports.sources.Manual',
  Scheduled: 'reports.sources.Scheduled',
} as const satisfies Record<RunSource, TranslationKey>;

export const FORMAT_LABEL_KEYS = {
  Pdf: 'reports.formats.Pdf',
  Csv: 'reports.formats.Csv',
} as const satisfies Record<ReportFormat, TranslationKey>;

/** A document for the PDF, a grid for the CSV — the format chips' glyphs (X2). */
export const FORMAT_ICONS = {
  Pdf: 'file-06',
  Csv: 'table',
} as const satisfies Record<ReportFormat, IconName>;

export const SCHEDULE_STATUS_LABEL_KEYS = {
  Active: 'reports.schedules.statuses.Active',
  Paused: 'reports.schedules.statuses.Paused',
  Ended: 'reports.schedules.statuses.Ended',
} as const satisfies Record<ScheduleStatus, TranslationKey>;

/** Paused is on hold, not at fault — yellow, not warning (X4). */
export const SCHEDULE_STATUS_COLORS = {
  Active: 'success',
  Paused: 'yellow',
  Ended: 'gray',
} as const satisfies Record<ScheduleStatus, BadgeColor>;

export const FREQUENCY_LABEL_KEYS = {
  Daily: 'reports.frequencies.Daily',
  Weekly: 'reports.frequencies.Weekly',
  Monthly: 'reports.frequencies.Monthly',
  Quarterly: 'reports.frequencies.Quarterly',
} as const satisfies Record<ScheduleFrequency, TranslationKey>;

export const DELIVERY_STATUS_LABEL_KEYS = {
  Pending: 'reports.detail.delivery.statuses.Pending',
  Delivered: 'reports.detail.delivery.statuses.Delivered',
  Failed: 'reports.detail.delivery.statuses.Failed',
  Unauthorized: 'reports.detail.delivery.statuses.Unauthorized',
  Skipped: 'reports.detail.delivery.statuses.Skipped',
} as const satisfies Record<DeliveryStatus, TranslationKey>;

/**
 * Delivered reads as done; "Access revoked" (Unauthorized) and Failed as errors (D11); Pending and
 * Skipped — nothing went wrong, nothing was sent yet or at all — as neutral.
 */
export const DELIVERY_STATUS_COLORS = {
  Pending: 'gray',
  Delivered: 'success',
  Failed: 'error',
  Unauthorized: 'error',
  Skipped: 'gray',
} as const satisfies Record<DeliveryStatus, BadgeColor>;

export { isOneOf } from './reportsGuards';

/** A run that may still change — the detail polls it and the list refreshes while one is shown. */
export function isActiveRun(run: Pick<ReportRunListItemDto, 'status'> | undefined): boolean {
  return run !== undefined && ACTIVE_RUN_STATUSES.includes(run.status);
}

export function fileStateOf(run: Pick<ReportRunListItemDto, 'formats'>, format: ReportFormat) {
  return format === 'Pdf' ? run.formats.pdf : run.formats.csv;
}

/** The formats a run was asked for, in display order (PDF first, as in Figma). */
export function requestedFormats(run: Pick<ReportRunListItemDto, 'formats'>): ReportFormat[] {
  return (['Pdf', 'Csv'] as const).filter((format) => fileStateOf(run, format) !== 'NotRequested');
}

/** Why a requested format cannot be downloaded right now, or `undefined` when it can. */
export type DownloadBlock = 'notReady' | 'noData' | 'expired' | 'failed';

export function downloadBlock(state: FileState): DownloadBlock | undefined {
  switch (state) {
    case 'Available':
      return undefined;
    case 'Pending':
    case 'NotRequested':
      return 'notReady';
    case 'NoData':
      return 'noData';
    case 'Expired':
      return 'expired';
    case 'Failed':
      return 'failed';
  }
}

/** A Failed or Expired run can be generated again (HS-1595 `retry`). */
export function canRetry(run: Pick<ReportRunListItemDto, 'status'>): boolean {
  return run.status === 'Failed' || run.status === 'Expired';
}

/** `RPT-3058` — the run number as the design prints it. */
export function runNumberLabel(number: number): string {
  return `RPT-${String(number)}`;
}

/** The templates a user may pick: enabled ones, in the server's order. */
export function selectableTemplates(templates: readonly ReportTemplateDto[]): ReportTemplateDto[] {
  return templates.filter((template) => template.enabled);
}

export function findTemplate(
  templates: readonly ReportTemplateDto[] | undefined,
  key: string,
): ReportTemplateDto | undefined {
  return templates?.find((template) => template.key === key);
}

/**
 * The laboratory a run covers, or `undefined` when its scope is not a laboratory — an institution
 * or group run has no laboratory, and its scope's name must not be shown as one.
 */
export function labNameOf(scope: Pick<ScopeRefDto, 'type' | 'name'>): string | undefined {
  return scope.type === 'Lab' ? scope.name : undefined;
}

/** A template that reports on one laboratory needs one picked; Institution scope does not. */
export function needsLab(template: Pick<ReportTemplateDto, 'scopeTypes'> | undefined): boolean {
  return template !== undefined && !template.scopeTypes.includes('Institution');
}
